import {
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  CardProps,
  Flex,
  IconButton,
  Link,
  LinkBox,
  useDisclosure,
} from "@chakra-ui/react";
import { NostrEvent } from "nostr-tools";
import { memo, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";

import { getThreadReferences } from "../../../helpers/nostr/event";
import useEventIntersectionRef from "../../../hooks/use-event-intersection-ref";
import { ExpandProvider } from "../../../providers/local/expanded";
import { ContentSettingsProvider } from "../../../providers/local/content-settings";
import { getSharableEventAddress } from "../../../services/relay-hints";
import ReplyForm from "../../../views/thread/components/reply-form";
import HoverLinkOverlay from "../../hover-link-overlay";
import { ReplyIcon } from "../../icons";
import Timestamp from "../../timestamp";
import UserAvatarLink from "../../user/user-avatar-link";
import UserLink from "../../user/user-link";
import useEventTipInfo from "../../../hooks/use-event-tip-info";
import EventZapButton from "../../zap/event-zap-button";
import BookmarkEventButton from "../../note/bookmark-button";
import NoteMenu from "../../note/note-menu";
import ShowMoreContainer from "../../note/show-more-container";
import EventShareButton from "./components/event-share-button";
import ReplyContext from "./components/reply-context";
import NoteContentWithWarning from "./note-content-with-warning";

export type TimelineNoteProps = Omit<CardProps, "children"> & {
  event: NostrEvent;
  variant?: CardProps["variant"];
  showReplyButton?: boolean;
  showReplyLine?: boolean;
  hideDrawerButton?: boolean;
  registerIntersectionEntity?: boolean;
  clickable?: boolean;
};
export function TimelineNote({
  event,
  variant = "unstyled",
  showReplyLine = true,
  hideDrawerButton,
  registerIntersectionEntity = true,
  clickable = true,
  ...props
}: TimelineNoteProps) {
  const replyForm = useDisclosure();
  const { total, latestTipTime } = useEventTipInfo(event);

  const glowStyle = useMemo(() => {
    if (total === 0) return {};
    const ageSeconds = Math.max(0, Date.now() / 1000 - latestTipTime);
    const opacity = Math.max(0.15, 1 - ageSeconds / 86400);
    const isRed = total >= 100_000_000;
    const r = isRed ? 239 : 66;
    const g = isRed ? 68 : 153;
    const b = isRed ? 68 : 225;
    return {
      boxShadow: `0 0 ${10 + opacity * 10}px rgba(${r}, ${g}, ${b}, ${opacity * 0.5})`,
      borderLeftWidth: "3px",
      borderLeftColor: `rgba(${r}, ${g}, ${b}, ${opacity})`,
    };
  }, [total, latestTipTime]);

  const ref = useEventIntersectionRef(event);

  return (
    <ContentSettingsProvider event={event}>
      <ExpandProvider>
        <Flex
          direction="column"
          borderBottomWidth="1px"
          borderColor="var(--chakra-colors-chakra-border-color)"
          {...glowStyle}
          {...props}
        >
          <Card
            as={LinkBox}
            variant={variant}
            ref={registerIntersectionEntity ? ref : undefined}
            data-event-id={event.id}
          >
            {clickable && <HoverLinkOverlay as={RouterLink} to={`/n/${getSharableEventAddress(event)}`} />}
            <CardHeader p="2">
              <Flex flex="1" gap="2" alignItems="center">
                <UserAvatarLink pubkey={event.pubkey} size="sm" />
                <UserLink pubkey={event.pubkey} isTruncated fontWeight="bold" fontSize="lg" />
                <Link as={RouterLink} whiteSpace="nowrap" color="current" to={`/n/${getSharableEventAddress(event)}`}>
                  <Timestamp timestamp={event.created_at} />
                </Link>
                <Flex grow={1} />
              </Flex>
              {showReplyLine && <ReplyContext event={event} />}
            </CardHeader>
            <CardBody as={ShowMoreContainer} px="2">
              <NoteContentWithWarning event={event} />
            </CardBody>
          </Card>
          <ButtonGroup size="sm" variant="ghost" zIndex={1} w="full" justifyContent="space-between" px="2" py="1">
            <IconButton icon={<ReplyIcon />} aria-label="Reply" title="Reply" onClick={replyForm.onOpen} />
            <EventShareButton event={event} />
            <EventZapButton event={event} />
            <BookmarkEventButton event={event} aria-label="Bookmark note" />
            <NoteMenu event={event} aria-label="More Options" />
          </ButtonGroup>
        </Flex>
      </ExpandProvider>
      {replyForm.isOpen && (
        <ReplyForm
          item={{ event, replies: new Set(), refs: getThreadReferences(event) }}
          onCancel={replyForm.onClose}
          onSubmitted={replyForm.onClose}
        />
      )}
    </ContentSettingsProvider>
  );
}

export default memo(TimelineNote);
